import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router';

import { PendingRewardsType } from '@src/types/vaults/vault.types';
import {
  INITIAL_VAULT_CONTEXT,
  VaultContextType,
  VaultFarmsV3InfoType,
  VaultInfoType,
} from '@src/types/vaults/vaultContext.types';
import { Address } from 'viem';

import {
  useBalance,
  useERC20Balances,
} from '@services/contract/common/erc20.contract';
import { useUserNfts } from '@services/contract/vault/NonfungiblePositionManager.contract';
import {
  useFarmsPendingRewards,
  usePriceRangeInfo,
} from '@services/contract/vault/farmConnector.contract';
import {
  usePositionManagerInfo,
  useUserNftInfo,
} from '@services/contract/vault/liquidityHelperV3.contract';
import { useV3PoolSqrtPrice } from '@services/contract/vault/poolV3.contract';
import { useAvailableToClaim } from '@services/contract/vault/profitStorage.contract';
import {
  useHarvestInfo,
  useVaultDetails,
  useVaultUserBalance,
} from '@services/contract/vault/vault.contract';

import { useFactories } from '@contexts/vaults/VaultFactoriesContext';
import { useVaultTokens } from '@contexts/vaults/VaultTokensContext';

import { useViewMode } from '@hooks/vaults/useViewMode';

import { getTotalValueByKey } from '@helpers/common/array.helpers';
import {
  getPriceRatioAtoB,
  normalizeDepositTokens,
  normalizePendingRewards,
  normalizeVaultFarmsInfo,
  normalizeWithdrawTokens,
  normalizeZapperTokens,
} from '@helpers/vaults/vault.helpers';

import { toBN } from '@utils/formaters.utils';
import { isAddressesEqual } from '@utils/tokens.utils';

import { ZERO_ADDRESS } from '@constants/common/chain.constants';
import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { PATH_VAULT } from '@constants/common/routes.constants';
import {
  DEFAULT_VAULT_DIVIDER,
  MAX_FARMS_GRID,
  MAX_FARMS_LIST,
} from '@constants/vaults/common.constants';
import { TokenType, V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';

import { LegacyVaultsDto } from '@dto/vaults/vaults.dto';

export type VaultActionsType = {
  onOpenVault: () => void;
  onUpdateVault: () => Promise<void>;
};

export let vaultActions: VaultActionsType;

export const VaultContext = createContext<VaultContextType>(
  INITIAL_VAULT_CONTEXT,
);

export const useVault = (): VaultContextType => useContext(VaultContext);

interface Props extends PropsWithChildren {
  vault: LegacyVaultsDto;
  onUpdate?: () => Promise<void>;
  isFullInfo?: boolean;
}

const VaultContextProvider: FC<Props> = ({
  vault,
  children,
  onUpdate,
  isFullInfo,
}) => {
  const navigate = useNavigate();
  const { isListMode } = useViewMode();
  const { tokens } = useVaultTokens();
  const { vaultFactoriesFarmsMap, vaultFactoriesById } = useFactories();

  const { totalSupply, refetch: refetchVaultDetails } = useVaultDetails({
    vault,
    isFullInfo,
  });

  const { userBalance, refetch: refetchUserBalance } = useVaultUserBalance({
    vault,
    isFullInfo,
  });

  const factoryFarms = useMemo(
    () => vaultFactoriesFarmsMap[vault.vaultFactoryId],
    [vault, vaultFactoriesFarmsMap],
  );

  const vaultFactory = useMemo(
    () => vaultFactoriesById[vault.vaultFactoryId],
    [vault, vaultFactoriesById],
  );

  const vaultFarms = useMemo(
    () => normalizeVaultFarmsInfo({ vault, factoryFarms, tokens }),
    [vault, factoryFarms, tokens],
  );

  const vaultV3Farms = useMemo(() => {
    if (!vaultFarms.length) return [];

    return vaultFarms.filter((farm) =>
      V3_TOKENS_TYPE.includes(farm.farmInfo.stakingTokenType),
    );
  }, [vaultFarms]);
  //
  // const zapperTokens = useMemo(
  //   () => normalizeZapperTokens({ vault, vaultFactory, tokens }),
  //   [vault, vaultFactory, tokens],
  // );
  //
  // const isEnableLpDeposit = useMemo(
  //   () =>
  //     vaultFarms.length &&
  //     vaultFarms.every(
  //       (item) => !V3_TOKENS_TYPE.includes(item.farmInfo.stakingTokenType),
  //     ),
  //   [vaultFarms],
  // );
  //
  // const isEnableNftDeposit = useMemo(
  //   () =>
  //     vaultFarms.length === 1 &&
  //     vaultFarms.every((item) =>
  //       V3_TOKENS_TYPE.includes(item.farmInfo.stakingTokenType),
  //     ),
  //   [vaultFarms],
  // );
  //
  // const farmsV2StakingTokens = useMemo(() => {
  //   if (!isEnableLpDeposit) return [];
  //
  //   return vaultFarms.map((item) => item.farmInfo.stakingToken);
  // }, [isEnableLpDeposit, vaultFarms]);
  //
  // const farmsV3StakingTokens = useMemo(() => {
  //   if (!isEnableNftDeposit) return [];
  //
  //   return vaultFarms.map((item) => item.farmInfo.stakingToken);
  // }, [isEnableNftDeposit, vaultFarms]);
  //
  // const normalizeDepositTokensForBalance = useMemo(() => {
  //   if (!isFullInfo) return [];
  //
  //   const formatTokens = [...zapperTokens, ...farmsV2StakingTokens].map(
  //     ({ address, chainId }) => ({
  //       address: address as Address,
  //       chainId,
  //     }),
  //   );
  //   return formatTokens.filter(
  //     (item) => !isAddressesEqual(item.address, ZERO_ADDRESS),
  //   );
  // }, [zapperTokens, farmsV2StakingTokens, isFullInfo]);

  const {
    availableToClaim,
    isLoading: isLoadingAvailableToClaim,
    refetch: refetchAvailableToClaim,
  } = useAvailableToClaim(vault);

  const {
    harvestInfo,
    isLoading: isLoadingHarvest,
    refetch: refetchHarvestInfo,
  } = useHarvestInfo({ vault, tokens });

  // const { farmsPendingRewards, refetch: refetchFarmsPendingRewards } =
  //   useFarmsPendingRewards({
  //     vault,
  //     vaultFarms,
  //     tokens,
  //     isFullInfo,
  //   });

  const { positionManagerInfo, refetch: refetchPositionManagerInfo } =
    usePositionManagerInfo({
      vaultFarms,
      vaultFactory,
      tokens,
      isFullInfo,
      vault,
    });
  const { priceRangeInfo, refetch: refetchPriceRangeInfo } = usePriceRangeInfo({
    vaultFarms,
    isFullInfo,
  });

  const { sqrtPriceData, refetch: refetchSqrtPriceData } = useV3PoolSqrtPrice({
    vaultFarms,
    isFullInfo,
  });

  // const { tokensIds, refetch: refetchUserNfts } = useUserNfts({
  //   isEnableNftDeposit,
  //   vaultFarms,
  //   isFullInfo,
  //   vault,
  // });

  // const { userNftInfo, refetch: refetchUserNftInfo } = useUserNftInfo({
  //   tokensIds,
  //   vaultFarms,
  //   isFullInfo,
  //   isEnableNftDeposit,
  //   vaultFactory,
  //   tokens,
  // });

  // const { balances: tokensBalances, refetch: refetchTokensBalances } =
  //   useERC20Balances(normalizeDepositTokensForBalance);

  // const { balance: nativeBalance, refetch: refetchNativeBalance } = useBalance({
  //   tokenAddress: isFullInfo ? ZERO_ADDRESS : undefined,
  //   chainId: vault.chainId,
  // });

  // const depositTokens = useMemo(
  //   () =>
  //     normalizeDepositTokens({
  //       zapperTokens,
  //       farmsV2StakingTokens,
  //       farmsV3StakingTokens,
  //       userNftInfo,
  //       nativeBalance,
  //       tokens,
  //       depositTokensBalances: tokensBalances,
  //     }),
  //   [
  //     tokens,
  //     nativeBalance,
  //     zapperTokens,
  //     userNftInfo,
  //     farmsV2StakingTokens,
  //     tokensBalances,
  //     farmsV3StakingTokens,
  //   ],
  // );

  const farmsV3Info: VaultFarmsV3InfoType = useMemo(
    () =>
      Object.keys(positionManagerInfo).reduce((acc, item) => {
        const _positionManagerInfo = positionManagerInfo[Number(item)];
        const _priceRangeInfo = priceRangeInfo[Number(item)];
        const _sqrtPriceData = sqrtPriceData[Number(item)];

        const { token0, token1 } = _positionManagerInfo;
        const { rateTokenAToTokenB, rateTokenBToTokenA } = getPriceRatioAtoB(
          _sqrtPriceData?.sqrtPriceX96,
          token0,
          token1,
        );

        return {
          ...acc,
          [item]: {
            ..._positionManagerInfo,
            ..._priceRangeInfo,
            ..._sqrtPriceData,
            priceToken0PerToken1: rateTokenAToTokenB,
            priceToken1PerToken0: rateTokenBToTokenA,
          },
        };
      }, {}),
    [positionManagerInfo, priceRangeInfo, sqrtPriceData],
  );

  // const withdrawTokens = useMemo(
  //   () =>
  //     normalizeWithdrawTokens({
  //       zapperTokens,
  //       tokensBalances,
  //       nativeBalance,
  //       vaultFarms,
  //       tokens,
  //     }),
  //   [zapperTokens, nativeBalance, tokensBalances, vaultFarms, tokens],
  // );

  const profitToken = useMemo(() => {
    if (!tokens || !vault) return null;

    const token = tokens.profit[vault.chainId][vault.profitToken];
    if (token.type === TokenType.ETF) {
      const { underlying } = token;
      const tokenList = tokens.etf[vault.chainId];

      return {
        ...token,
        tokenList: underlying ? tokenList[underlying].innerTokens : [],
      };
    }
    return token;
  }, [tokens, vault]);

  const maxFarmsCount = useMemo(
    () => (isListMode ? MAX_FARMS_LIST : MAX_FARMS_GRID),
    [isListMode],
  );

  const farmsCounter = useMemo(() => {
    if (toBN(vaultFarms.length).lte(maxFarmsCount)) {
      return null;
    }

    return toBN(vaultFarms.length).minus(maxFarmsCount).toString();
  }, [maxFarmsCount, vaultFarms]);

  const claimValueUsd = useMemo(() => {
    if (!profitToken || !availableToClaim || !harvestInfo) return toBN(0);

    return toBN(availableToClaim, profitToken?.decimals)
      .times(profitToken.priceInUsd)
      .plus(
        harvestInfo.profitAmountUsd.isNaN() ? 0 : harvestInfo.profitAmountUsd,
      );
  }, [profitToken, availableToClaim, harvestInfo]);

  // const pendingRewards = useMemo(() => {
  //   const feePendingRewards = Object.keys(positionManagerInfo).reduce(
  //     (acc, item) => {
  //       const farm = vaultFarms.find(
  //         (farmItem) => Number(farmItem.farm.farmId) === Number(item),
  //       );
  //
  //       if (
  //         farm?.farmInfo.stakingTokenType ===
  //         TokenType.ERC721_AERODROME_CL_GAUGE
  //       ) {
  //         return acc;
  //       }
  //
  //       return [...acc, positionManagerInfo[Number(item)].feePendingRewards];
  //     },
  //     [] as PendingRewardsType[][],
  //   );
  //
  //   const feePendingRewardsFlat = feePendingRewards.flat();
  //
  //   return normalizePendingRewards({
  //     farmsPendingRewards,
  //     feePendingRewards: feePendingRewardsFlat,
  //     vault,
  //   });
  // }, [positionManagerInfo, farmsPendingRewards, vault]);

  const vaultInfo: VaultInfoType = useMemo(() => {
    const vaultTitle = vault.title || `#${vault.id}`;
    const _availableToClaim = toBN(availableToClaim, profitToken?.decimals);
    const takeProfitPercent = toBN(vault.takeProfitPercent || 0).times(
      PERCENT_DIVIDER,
    );
    const farmsTvlUsd = getTotalValueByKey(
      vaultFarms.map((farm) => farm.farmInfo),
      'tvlUsd',
    );
    const tvlUsd = toBN(vault.tvlUsd || 0);
    const availableToClaimUsd = _availableToClaim.times(
      profitToken?.priceInUsd || 0,
    );
    const profitUsd = harvestInfo.profitAmountUsd.plus(availableToClaimUsd);

    return {
      harvestInfoLoading: isLoadingHarvest,
      availableToClaimLoading: isLoadingAvailableToClaim,

      vaultFarms,
      vaultV3Farms,
      vaultFarmsV3Info: farmsV3Info,
      vaultTitle,
      profitToken,
      harvestInfo,

      vaultFactory,

      farmsCounter,
      maxFarmsCount,

      availableToClaim: _availableToClaim,
      availableToClaimUsd,
      claimValueUsd,
      profitUsd,

      takeProfitPercent,
      compoundPercent: toBN(PERCENT_DIVIDER).minus(takeProfitPercent),

      // isSavedVault: vault.isSaved,
      // isBoostedVault: vault.isBoosted,
      isFullCompound: toBN(vault.takeProfitPercent).equals(0),
      isFullProfit: toBN(vault.takeProfitPercent).equals(1),
      isInitialized: !totalSupply.isZero(),
      hasAccountBalance: toBN(vault.accountBalance).gt(0),
      isSingleFarmVault: toBN(vaultFarms.length).equals(1),
      isNoAccountDeposit: toBN(vault.accountDepositTvl || 0).equals(0),
      withV3Farm: vaultFarms.some((farm) =>
        V3_TOKENS_TYPE.includes(farm.farmInfo.stakingTokenType),
      ),

      farmsTvlUsd,
      tvlUsd,
      investUsd: toBN(vault.accountDepositTvl || 0),
      accountBalance: toBN(vault.accountBalance || 0),
      syntheticUsd: toBN(vault.tvlUsd).div(
        toBN(vault.totalSupply, DEFAULT_VAULT_DIVIDER),
      ),
      totalSupply,
      userBalance,

      // apy: toBN(vault.apy || 0).times(PERCENT_DIVIDER),
      // netApy: toBN(vault.netApy || 0).times(PERCENT_DIVIDER),
      // dailyApy: toBN(vault.dailyApy || 0).times(PERCENT_DIVIDER),
      // boostApy: toBN(vault.boostApy || 0).times(PERCENT_DIVIDER),
      // boostDailyApy: toBN(vault.boostDailyApy || 0).times(PERCENT_DIVIDER),

      // depositTokens,
      tokens,
      // withdrawTokens,
      // pendingRewards,
      // userNftInfo,
    };
  }, [
    vault,
    vaultFarms,
    harvestInfo,
    profitToken,
    farmsCounter,
    vaultFactory,
    maxFarmsCount,
    availableToClaim,
    isLoadingHarvest,
    isLoadingAvailableToClaim,
    farmsV3Info,
    // depositTokens,
    // userNftInfo,
    totalSupply,
    claimValueUsd,
    // withdrawTokens,
    // pendingRewards,
    vaultV3Farms,
    tokens,
  ]);

  vaultActions = {
    onOpenVault: () => {
      navigate(PATH_VAULT.replace(':id', String(vault.id)));
    },
    onUpdateVault: async () => {
      // await refetchVaultDetails();
      await refetchUserBalance();
      // if (onUpdate) {
      //   await Promise.all([
      //     // onUpdate(),
      //     // refetchAvailableToClaim(),
      //     // refetchHarvestInfo(),
      //     // refetchFarmsPendingRewards(),
      //     // refetchPositionManagerInfo(),
      //     // refetchPriceRangeInfo(),
      //     // refetchSqrtPriceData(),
      //     // refetchUserNfts(),
      //     // refetchUserNftInfo(),
      //     // refetchTokensBalances(),
      //     // refetchNativeBalance(),
      //     refetchVaultDetails(),
      //   ]);
      // }
    },
  };

  return (
    <VaultContext.Provider
      value={{
        vault,
        vaultInfo,
      }}
    >
      {children}
    </VaultContext.Provider>
  );
};

export default VaultContextProvider;
